import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AuxiliaryDataTableComponent, ObjectType } from '@vendasta/auxiliary-data-components';
import { PageService } from '@vendasta/galaxy/page/src/page.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { IAMService, User } from '@vendasta/iam';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, publishReplay, refCount, switchMap, take, tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../acquisition-widget/acquisition-widget-overview.component';
import { AppConfigService } from '../../app-config.service';
import { SELECT_WELCOME_EMAIL_LOCALES } from '../../constants';
import { AccessService, Feature, StubAccessService } from '../../core/access';
import { ResetPasswordService } from '../reset-password.service';
import { UserService } from '../user.service';
import { VbcApiService, VbcUser } from '../vbc-api.service';

const SNACKBAR_DURATION = 4000;

interface UserInfo {
  firstName: string;
  lastName: string;
  greetingName: string;
  email: string;
  phone: string;
  language: string;
}

@Component({
  templateUrl: `./edit.component.html`,
  styleUrls: ['./edit.component.scss'],
  standalone: false,
})
export class BusinessUserEditComponent implements OnInit {
  @ViewChild('auxiliarydatatable')
  private auxiliaryDataTable: AuxiliaryDataTableComponent;
  auxiliaryDataObjectType: ObjectType = 'user';

  isUpdatingUser = false;

  emailLanguages = SELECT_WELCOME_EMAIL_LOCALES;

  userFormGroup = new UntypedFormGroup({
    first_name: new UntypedFormControl(),
    last_name: new UntypedFormControl(),
    greeting_name: new UntypedFormControl(),
    user_email: new UntypedFormControl(),
    phone: new UntypedFormControl(),
    languages: new UntypedFormControl(SELECT_WELCOME_EMAIL_LOCALES[0].value),
  });
  private userId$: Observable<string>;
  iamUserId$: Observable<string>;
  private iamUser$: Observable<User>;
  private vbcUser$: Observable<VbcUser>;
  user$: Observable<UserInfo>;
  hasCustomFieldAccess$ = this.accessService.hasAccessToFeature(Feature.customFields);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private user: UserService,
    private resetPasswordService: ResetPasswordService,
    private appConfigService: AppConfigService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private translationService: TranslateService,
    @Inject('PARTNER_ID') public partnerId$: Observable<string>,
    private iamService: IAMService,
    private vbcApiService: VbcApiService,
    private readonly trackingService: ProductAnalyticsService,
    private readonly pageService: PageService,
    @Inject(StubAccessService) private readonly accessService: AccessService,
  ) {}

  ngOnInit(): void {
    this.userId$ = this.route.paramMap.pipe(
      map((params) => params.get('userId')),
      publishReplay(1),
      refCount(),
    );

    this.vbcUser$ = combineLatest([this.partnerId$, this.userId$]).pipe(
      switchMap(([partnerId, userId]) => this.vbcApiService.getUser(partnerId, userId)),
      publishReplay(1),
      refCount(),
    );

    this.iamUserId$ = this.vbcUser$.pipe(
      map((user) => user?.unifiedUserId),
      filter((id) => !!id),
    );
    this.iamUser$ = this.vbcUser$.pipe(
      switchMap((user) => {
        const userId = user?.unifiedUserId || '';
        return this.iamService.getMultiUsers([{ userId }]);
      }),
      map((users) => {
        return users[0];
      }),
      tap((user) => {
        this.userFormGroup.patchValue({
          languages: user.languageCode,
        });
      }),
      publishReplay(1),
      refCount(),
    );

    this.user$ = combineLatest([this.vbcUser$, this.iamUser$]).pipe(
      map(([vbc, iam]): UserInfo => {
        //try to find the language in existing languages
        const matchedLanguage = this.findClosestLanguageCode(iam.languageCode);
        return {
          firstName: vbc?.firstName || '',
          lastName: vbc?.lastName || '',
          greetingName: iam?.greetingName || '',
          email: vbc?.email,
          phone: vbc?.phone,
          language: matchedLanguage,
        };
      }),
      tap((user) => {
        this.userFormGroup.patchValue({
          first_name: user?.firstName,
          last_name: user?.lastName,
          greeting_name: user?.greetingName,
          user_email: user?.email,
          languages: user?.language || SELECT_WELCOME_EMAIL_LOCALES[0].value,
        });
      }),
    );
  }

  public resetPassword(): void {
    const confirmDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '620px',
    });
    confirmDialogRef.componentInstance.dialogData = {
      title: this.translationService.instant('USERS.ACTIONS.SEND_RESET_PASSWORD_EMAIL_PROMPT'),
      body: this.translationService.instant('USERS.ACTIONS.SEND_RESET_PASSWORD_EMAIL_CONFIRMATION'),
      buttonText: this.translationService.instant('COMMON.ACTION_LABELS.SEND'),
      buttonClass: 'button-normal',
    };
    confirmDialogRef
      .afterClosed()
      .pipe(
        take(1),
        filter((val) => val),
        switchMap(() => this.iamUser$),
      )
      .subscribe((user: User) => {
        this.resetPasswordService
          .resetPassword({
            email: user.email,
            partnerId: this.appConfigService.config.partnerId,
          })
          .subscribe((result) => {
            if (result) {
              this.snackbarService.successSnack(
                this.translationService.instant('USERS.ACTIONS.SEND_RESET_PASSWORD_EMAIL_SUCCESS'),
              );
            } else {
              this.snackbarService.errorSnack(
                this.translationService.instant('USERS.ERRORS.RESET_PASSWORD_FAILED'),
                null,
                SNACKBAR_DURATION,
              );
            }
          });
      });
  }

  updateUser(): void {
    this.isUpdatingUser = true;
    const updateCustomFields$ = this.hasCustomFieldAccess$.pipe(
      switchMap((hasCustomFieldAccess) => {
        if (hasCustomFieldAccess) {
          return this.updateCustomFields();
        }
        return of(true);
      }),
    );

    combineLatest([this.updateFixedFields(), updateCustomFields$])
      .pipe(take(1))
      .subscribe({
        next: ([successFixed, successCustom]) => {
          this.isUpdatingUser = false;
          if (successFixed && successCustom) {
            this.snackbarService.openSuccessSnack('EDIT_BUSINESS_USER_PAGE.SUBMIT_USER_SUCCESS');
            this.router.navigateByUrl(this.routeToNavigateTo());
          } else {
            this.snackbarService.openErrorSnack('EDIT_BUSINESS_USER_PAGE.SUBMIT_USER_ERROR', {
              duration: SNACKBAR_DURATION,
            });
          }
        },
      });
  }

  private updateFixedFields(): Observable<boolean> {
    return combineLatest([this.partnerId$, this.userId$]).pipe(
      switchMap(([partnerId, userId]) => {
        return this.vbcApiService.updateUser(
          partnerId,
          userId,
          this.userFormGroup.get('first_name').value || '',
          this.userFormGroup.get('last_name').value || '',
          this.userFormGroup.get('greeting_name').value || '',
          this.userFormGroup.get('user_email').value,
          this.userFormGroup.get('phone').value,
          this.userFormGroup.get('languages').value,
        );
      }),
      take(1),
    );
  }

  private updateCustomFields(): Observable<boolean> {
    this.posthogTrackCustomFieldEvent('start');
    return this.auxiliaryDataTable.saveChanges().pipe(
      map(() => true),
      catchError(() => {
        this.posthogTrackCustomFieldEvent('fail');
        return of(false);
      }),
      tap((success) => {
        if (success) {
          this.posthogTrackCustomFieldEvent('success');
        }
      }),
    );
  }

  private posthogTrackCustomFieldEvent(suffix: string): void {
    if (this.auxiliaryDataTable.hasChanges()) {
      this.trackingService.trackEvent(
        `CustomFields:edit-user-custom-field-data-${suffix}`,
        'CustomFields',
        `edit-user-custom-field-data-${suffix}`,
      );
    }
  }

  private findClosestLanguageCode(lc: string): string {
    //return early if there is no language code to match
    if (!lc) {
      return '';
    }
    //try to find the language in existing languages
    let matchedLanguage = this.emailLanguages.find((t) => t.value == lc);
    //If the language code isn't in our dictionary, try trimming the code and seeing if we can find it
    if (matchedLanguage == undefined) {
      matchedLanguage = this.emailLanguages.find((t) => t.value == lc.split('-')[0]);
    }
    return matchedLanguage?.value || lc;
  }

  private routeToNavigateTo(): string {
    return this.pageService.getPreviousPageUrl('/bc-admin');
  }

  cancelUpdateUser(): void {
    this.posthogTrackCustomFieldEvent('cancel');
    this.router.navigateByUrl(this.routeToNavigateTo());
  }
}
